export interface ISafeHistoryInterval {
  timeFrom: number;
  timeTo: number;
  groupByInterval: number;
}

interface IBuildSafeHistoryIntervalParams {
  rawTimeFrom: number;
  groupByInterval: number;
  maxIntervals?: number;
  useNextUtcDayBoundary?: boolean;
  roundTimeToUpByGroup?: boolean;
}

function getNowTimestamp(): number {
  return Math.floor(Date.now() / 1000);
}

function getNextUtcDayStartTimestamp(): number {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  tomorrow.setUTCHours(0, 0, 0, 0);
  return Math.floor(tomorrow.getTime() / 1000);
}

function alignDown(value: number, step: number): number {
  return Math.floor(value / step) * step;
}

function alignUp(value: number, step: number): number {
  return Math.ceil(value / step) * step;
}

export function buildSafeHistoryInterval({
  rawTimeFrom,
  groupByInterval,
  maxIntervals = 3_200,
  useNextUtcDayBoundary = false,
  roundTimeToUpByGroup = false,
}: IBuildSafeHistoryIntervalParams): ISafeHistoryInterval {
  const safeGroupByInterval =
    Number.isFinite(groupByInterval) && groupByInterval > 0
      ? Math.floor(groupByInterval)
      : 1;

  let timeTo = useNextUtcDayBoundary
    ? getNextUtcDayStartTimestamp()
    : roundTimeToUpByGroup
      ? alignUp(getNowTimestamp(), safeGroupByInterval)
      : alignDown(getNowTimestamp(), safeGroupByInterval);
  if (timeTo <= safeGroupByInterval) {
    timeTo = safeGroupByInterval;
  }

  const safeMaxIntervals =
    Number.isFinite(maxIntervals) && maxIntervals > 0
      ? Math.floor(maxIntervals)
      : 3_200;

  const maxWindowFrom = timeTo - safeMaxIntervals * safeGroupByInterval;
  const rawFrom = rawTimeFrom > 0 ? rawTimeFrom : maxWindowFrom;

  let timeFrom = alignDown(rawFrom, safeGroupByInterval);
  if (timeFrom < maxWindowFrom) {
    timeFrom = maxWindowFrom;
  }
  if (timeFrom >= timeTo) {
    timeFrom = timeTo - safeGroupByInterval;
  }

  const intervals = Math.max(
    1,
    Math.min(
      safeMaxIntervals,
      Math.floor((timeTo - timeFrom) / safeGroupByInterval)
    )
  );
  timeFrom = timeTo - intervals * safeGroupByInterval;

  return {
    timeFrom,
    timeTo,
    groupByInterval: safeGroupByInterval,
  };
}
